import React from "react";
import {
  Box,
  Button,
  Grid2 as Grid,
  Paper,
  TextField,
  Typography,
  MenuItem,
  InputAdornment,
  Chip,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";

interface TaskFilterProps {
  searchTerm: string;
  category: string;
  isActive: boolean | "";
  priorityLevel: string;
  executionFrequency: string;
  sortBy: string;
  sortOrder: "asc" | "desc";
  onSearchTermChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onIsActiveChange: (value: boolean | "") => void;
  onPriorityLevelChange: (value: string) => void;
  onExecutionFrequencyChange: (value: string) => void;
  onSortByChange: (value: string) => void;
  onSortOrderChange: (value: "asc" | "desc") => void;
  onSearch: () => void;
  onReset: () => void;
}

export const TaskFilter: React.FC<TaskFilterProps> = ({
  searchTerm,
  category,
  isActive,
  priorityLevel,
  executionFrequency,
  sortBy,
  sortOrder,
  onSearchTermChange,
  onCategoryChange,
  onIsActiveChange,
  onPriorityLevelChange,
  onExecutionFrequencyChange,
  onSortByChange,
  onSortOrderChange,
  onSearch,
  onReset,
}) => {
  const hasActiveFilters =
    searchTerm ||
    category ||
    isActive !== "" ||
    priorityLevel ||
    executionFrequency ||
    sortBy !== "name";

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <FilterIcon color="primary" />
        <Typography variant="h6">Search & Filter</Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => onSearchTermChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <TextField
            fullWidth
            size="small"
            select
            label="Category"
            value={category}
            onChange={(e) => onCategoryChange(e.target.value)}
          >
            <MenuItem value="">All Categories</MenuItem>
            <MenuItem value="boss_baby">Boss Baby</MenuItem>
            <MenuItem value="automation">Automation</MenuItem>
            <MenuItem value="notification">Notification</MenuItem>
          </TextField>
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <TextField
            fullWidth
            size="small"
            select
            label="Status"
            value={isActive}
            onChange={(e) =>
              onIsActiveChange(
                e.target.value === "" ? "" : e.target.value === "true"
              )
            }
          >
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
          </TextField>
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <TextField
            fullWidth
            size="small"
            select
            label="Priority"
            value={priorityLevel}
            onChange={(e) => onPriorityLevelChange(e.target.value)}
          >
            <MenuItem value="">All Priorities</MenuItem>
            <MenuItem value="HIGH">High</MenuItem>
            <MenuItem value="MEDIUM">Medium</MenuItem>
            <MenuItem value="LOW">Low</MenuItem>
          </TextField>
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <TextField
            fullWidth
            size="small"
            select
            label="Frequency"
            value={executionFrequency}
            onChange={(e) => onExecutionFrequencyChange(e.target.value)}
          >
            <MenuItem value="">All Frequencies</MenuItem>
            <MenuItem value="DAILY">Daily</MenuItem>
            <MenuItem value="WEEKLY">Weekly</MenuItem>
            <MenuItem value="MONTHLY">Monthly</MenuItem>
          </TextField>
        </Grid>

        <Grid size={{ xs: 12, md: 1 }}>
          <TextField
            fullWidth
            size="small"
            select
            label="Sort"
            value={sortBy}
            onChange={(e) => onSortByChange(e.target.value)}
          >
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="createdAt">Created</MenuItem>
            <MenuItem value="priority">Priority</MenuItem>
          </TextField>
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", gap: 1 }}>
        <Button variant="contained" onClick={onSearch}>
          Search
        </Button>
        <Button variant="outlined" onClick={onReset}>
          Reset
        </Button>
      </Box>

      {/* Active Filters */}
      {hasActiveFilters && (
        <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            Active filters:
          </Typography>
          {searchTerm && (
            <Chip
              label={`Search: ${searchTerm}`}
              size="small"
              onDelete={() => onSearchTermChange("")}
            />
          )}
          {category && (
            <Chip
              label={`Category: ${category}`}
              size="small"
              onDelete={() => onCategoryChange("")}
            />
          )}
          {isActive !== "" && (
            <Chip
              label={`Status: ${isActive ? "Active" : "Inactive"}`}
              size="small"
              onDelete={() => onIsActiveChange("")}
            />
          )}
          {priorityLevel && (
            <Chip
              label={`Priority: ${priorityLevel}`}
              size="small"
              onDelete={() => onPriorityLevelChange("")}
            />
          )}
          {executionFrequency && (
            <Chip
              label={`Frequency: ${executionFrequency}`}
              size="small"
              onDelete={() => onExecutionFrequencyChange("")}
            />
          )}
          {sortBy !== "name" && (
            <Chip
              label={`Sort: ${sortBy}`}
              size="small"
              onDelete={() => onSortByChange("name")}
            />
          )}
        </Box>
      )}
    </Paper>
  );
};







